<template>
  <form class="needs-validation ml-0" novalidate @submit.prevent="submit">
    <h1 class="h2 mb-3">Committee Details</h1>
    <div class="text-center p-2" v-if="isLoading">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <div v-if="!isLoading">
      <div role="alert" class="alert alert-warning" v-if="errors.length">
        <div v-for="error in errors">{{ error }}</div>
      </div>
      <div role="alert" class="alert alert-warning" v-if="submitStatus === 'ERROR'">
        Please fill the form correctly.
      </div>
      <div role="alert" class="alert alert-success" v-if="submitStatus === 'SUCCESS'">
        The Committee data saved successfully.
      </div>
      <div role="alert" class="alert alert-warning" v-if="errorStatus">
        {{ errorStatus }}
      </div>

      <div class="row">
        <div class="col-12 col-md-6 form-group">
          <label for="committeeName">Committee Name <span class="text-danger">*</span></label>
          <input type="text" class="form-control" :class="{ 'is-invalid': $v.data.name.$error }" id="committeeName"
                 required
                 v-model.trim="$v.data.name.$model">
          <div class="invalid-tooltip">Please include the Committee Name.</div>
        </div>

        <div class="col-12 col-md-6 form-group">
          <label for="contactNameCreate">Contact Name <span class="text-danger">*</span></label>
          <input type="text"
            class="form-control"
            :class="{ 'is-invalid': $v.data.contact_name.$error || errorFields.contact_name === true }"
            id="contactNameCreate"
            required
            v-model=" $v.data.contact_name.$model">
          <div class="invalid-tooltip">Please include the Contact Name.</div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6 form-group">
          <label for="committeeLogoFile">Committee logo</label>
          <div class="custom-file">
            <input type="file"
                   class="form-control custom-file-input"
                   :name ="logo_file"
                   :class="{ 'is-invalid': logoError.fileSize }"
                   id="committeeLogoFile" accept="image/*" @change="logoUpload">
            <label class="custom-file-label font-weight-normal"
                   for="committeeLogoFile">{{ logo_name ? logo_name : 'Choose file...' }}</label>
            <small>jpg, jpeg, png, tiff, gif file type required.</small>
             <div class="invalid-tooltip" v-if="logoError.fileSize">Logo File may not be greater than 250 kilobytes</div>
            <button
              v-if="logo_name"
              type="button"
              class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
              @click="deleteLogoAttachment">
              <span class="sr-only">Delete File</span>
              <div class="btn-remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                  <g fill="currentColor">
                    <path
                      d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/>
                    <path
                      d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/>
                    <path
                      d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/>
                  </g>
                </svg>
              </div>
            </button>
          </div>
          <div class="attached-file-wrapper" v-if="this.data.logo_file_path">
            {{ this.data.logo_file_name }}
            <div class="btn-remove-file" @click="deleteExistsAttachment">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                <g fill="currentColor">
                  <path
                    d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path>
                </g>
              </svg>
            </div>
            <div class="btn-download-file" @click="downloadLogoFile(data.id)">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                <g fill="currentColor">
                  <path
                    d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"></path>
                  <path
                    d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"></path>
                </g>
              </svg>
            </div>
          </div>
        </div>

        <div class="col-12 col-md-6 form-group">
          <label for="picNumber">PIC number</label>
          <input type="text" class="form-control" id="picNumber" v-model.trim="$v.data.pic_number.$model">
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6 form-group">
          <label for="committeeName">Contact Email <span class="text-danger">*</span></label>
          <input type="text" class="form-control" id="committeeEmail"
                 :class="{ 'is-invalid': $v.data.contact_email.$error || errorFields.contact_email === true}"
                 @blur="$v.data.contact_email.$touch()" required
                 v-model.lazy="$v.data.contact_email.$model">
          <div class="invalid-tooltip">
            {{
              ($v.data.contact_email.$error || errorFields.contact_email === true) && errorStatus
                ? errorStatus
                : 'Please include the valid Email.'
            }}
          </div>
        </div>
        <div class="col-12 col-md-6 form-group">
          <label for="committeeName">Contact Phone <span class="text-danger">*</span></label>
          <input type="text" class="form-control" id="committeePhone"
                 :class="{ 'is-invalid': $v.data.contact_phone.$error }"
                 required
                 v-model.trim="$v.data.contact_phone.$model">
          <div class="invalid-tooltip" v-if="!$v.data.contact_phone.required">Please include the Phone number.</div>
          <div class="invalid-tooltip" v-if="!$v.data.contact_phone.phoneRegex">Please include the valid Phone number.</div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6 form-group">
          <label for="committeeName">ABN <span class="text-danger">*</span></label>
          <input type="text" class="form-control" id="committeeAbn" :class="{ 'is-invalid': $v.data.abn.$error }"
                 required
                 v-model.trim="$v.data.abn.$model">
          <div class="invalid-tooltip">Please include the ABN.</div>
        </div>
        <div class="col-12 col-md-6 form-group">
          <label for="committeeName">Cheques To <span class="text-danger">*</span></label>
          <input type="text" class="form-control" id="committeeChequesTo"
                 :class="{ 'is-invalid': $v.data.cheques_to.$error }"
                 required v-model.trim="$v.data.cheques_to.$model">
          <div class="invalid-tooltip">Please include the Cheques To.</div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6 form-group">
          <label for="committeeName">City <span class="text-danger">*</span></label>
          <input type="text" class="form-control" id="committeeCity" :class="{'is-invalid': $v.data.city.$error || errorFields.city === true}"
                 required
                 @input="findZone()"
                 v-model.trim="$v.data.city.$model">
          <div class="invalid-tooltip">Please include the valid city.</div>
        </div>

        <div class="col-12 col-md-6 form-group">
          <label for="state">State <span class="text-danger">*</span></label>
          <select class="form-control" id="state" required :class="{ 'is-invalid': $v.data.state.$error }"
                  v-model.trim="$v.data.state.$model">
            <option disabled></option>
            <option value="QLD">Queensland</option>
            <option value="NSW">New South Wales</option>
            <option value="VIC">Victoria</option>
            <option value="ACT">Australian Capital Territory</option>
            <option value="SA">South Australia</option>
            <option value="NT">Northern Territory</option>
            <option value="WA">Western Australia</option>
            <option value="TAS">Tasmania</option>
          </select>
          <div class="invalid-tooltip">Please include your State.</div>
        </div>
      </div>

      <div class="row">
        <div class="col-6 col-md-3 form-group">
          <label for="postcode">Postcode <span class="text-danger">*</span></label>
          <input type="text" class="form-control" id="postcode" maxlength="4" required pattern="\d{4}"
                 @input="findZone()" :class="{ 'is-invalid': $v.data.post_code.$error || errorFields.postal_code === true }"
                 v-model.trim="$v.data.post_code.$model">
          <div class="invalid-tooltip">Please include the valid postcode.</div>
        </div>
        <div class="col-6 col-md-3 form-group">
          <label for="zone">Zone <span class="text-danger">*</span></label>
          <select class="form-control" :class="{ 'is-invalid': $v.data.zone_id.$error }" id="zone"
                  v-model.trim="$v.data.zone_id.$model">
            <option :value="null">Unspecified</option>
            <option v-for="(zone, index) in $store.state.listZones.filter(zone => !zone.deleted_at)" :key="index"
                    :value="zone.id">{{ zone.code }}
            </option>
          </select>
          <div class="invalid-tooltip">Please include your Zone.</div>
          <div class="alert alert-warning alert-sm" v-if="zoneNotFound && !zoneNotSpecified">Unable to find matching
            Zone.
          </div>
          <div class="alert alert-warning alert-sm zone-alert" v-else-if="zoneNotSpecified && zoneErrorMessage">{{ zoneErrorMessage }}</div>
          <div class="alert alert-success alert-sm zone-alert" v-else-if="zoneFound">Found matching Zone.</div>
        </div>

        <div class="col-6 col-md-3 form-group">
          <label for="committeeName">Account Number <span class="text-danger">*</span></label>
          <input type="text" class="form-control" :class="{ 'is-invalid': $v.data.account_number.$error || errorFields.account_number === true }"
                 id="committeeAccountNumber"
                 required v-model.trim="$v.data.account_number.$model">
          <div class="invalid-tooltip">Please include correct the Account Number.</div>
        </div>
        <div class="col-6 col-md-3 form-group">
          <label for="committeeName">Address <span class="text-danger">*</span></label>
          <input type="text" class="form-control" id="committeeAddress"
                 :class="{ 'is-invalid': $v.data.address.$error || errorFields.line1 === true}"
                 required v-model.trim="$v.data.address.$model">
          <div class="invalid-tooltip">Please include the valid address.</div>
        </div>
      </div>

      <div class="row">
        <div class="col-6 col-md-3 form-group">
          <label for="committeeName">BSB <span class="text-danger">*</span></label>
          <input type="text" class="form-control"
                 :class="{ 'is-invalid': $v.data.bsb.$error || errorFields.routing_number === true}"
                 id="committeeBSB"
                 required
                 v-model.trim="$v.data.bsb.$model" maxlength="7"
                 @input="validateBankBsb"
          >
          <div class="invalid-tooltip" v-if="!$v.data.bsb.bsbRegex || errorFields.routing_number === true">Please include the valid BSB.</div>
          <small class="small-notes small-notes-per">Please enter the bank's BSB in the format XXX-YYY.</small>
        </div>
        <div class="col-6 col-md-3 form-group">
          <label for="committeeName">Bank Abbreviation</label>
          <input type="text" class="form-control" :class="{ 'is-invalid': $v.data.bank_abbreviation.$error }"
                 id="committeeBankAbbreviation" required v-model.trim="$v.data.bank_abbreviation.$model">
          <div class="invalid-tooltip">Please include the Bank Abbreviation.</div>
          <small class="small-notes small-notes-per">The bank abbreviation must be approved Financial Institution
            abbreviation.</small>
        </div>
        <div class="col-6 col-md-3 form-group gst_registered">
          <div class="custom-control custom-checkbox">
            <input type="checkbox" class="custom-control-input" id="gst_registered"
                   v-model.trim="$v.data.gst_registered.$model">
            <label class="custom-control-label" for="gst_registered">
              I am GST registered
            </label>
            <div class="invalid-tooltip">Please include true or false.</div>
          </div>
        </div>
      </div>

      <div class="row">

        <div class="col-6 col-md-3 form-group">
          <div><label class="">Credit Card Payments</label></div>
          <div class="custom-control custom-radio custom-control-inline">
            <input class="custom-control-input" type="radio" id="accept" value="1"
                   :checked="data.is_enabled_stripe == '1'" v-model="data.is_enabled_stripe">

            <label class="custom-control-label font-weight-normal" for="accept">Accept</label>
          </div>
          <div class="custom-control custom-radio custom-control-inline">
            <input class="custom-control-input" type="radio" id="notAccept" value="0"
                   :checked="data.is_enabled_stripe == '0'" v-model="data.is_enabled_stripe">

            <label class="custom-control-label font-weight-normal" for="notAccept">Not Accept</label>
          </div>
          <br>
          <small class="small-notes small-notes-per">To accept instant online payments such as credit card for nomination fees.</small>
        </div>
      </div>

      <div v-if="data.is_enabled_stripe == '1'">
        <div class="row">
          <div class="col-6 col-md-3 form-group">
            <label>Bank Owner First Name </label>
            <input type="text" class="form-control" required
                   :class="{ 'is-invalid': $v.data.bank_owner_first_name.$error }"
                   v-model.trim="$v.data.bank_owner_first_name.$model">
            <div class="invalid-tooltip">Please include the Bank Owner First Name.</div>
          </div>
          <div class="col-6 col-md-3 form-group">
            <label>Bank Owner Last Name </label>
            <input type="text" class="form-control" required
                   :class="{ 'is-invalid': $v.data.bank_owner_last_name.$error }"
                   v-model.trim="$v.data.bank_owner_last_name.$model">
            <div class="invalid-tooltip">Please include the Bank Owner Last Name.</div>
          </div>

        </div>

        <div class="row">
          <div class="col-6 col-md-3 form-group">
            <label>Bank Owner Day Of Birth </label>
            <input type="date" class="form-control form-date" id="bankOwnerDOB" required
                   :class="{ 'is-invalid': $v.data.bank_owner_dob.$error }"
                   v-model.trim="$v.data.bank_owner_dob.$model" :placeholder="'dd.mm.yyyy'"
                   pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">

            <div class="invalid-tooltip">Please include the Bank Owner Day Of Birth.</div>
          </div>
          <div class="col-6 col-md-3 form-group" v-if="!data.is_stripe_verified">
            <label for="bankIdentityFile">Bank Identity File</label>
            <div class="custom-file">
              <input type="file"
                     class="form-control custom-file-input"
                     :class="{ 'is-invalid': fileError.required || fileError.fileSize }"
                     id="bankIdentityFile" accept="image/*, application/pdf" @change="fileUpload">
              <label class="custom-file-label font-weight-normal"
                     for="bankIdentityFile">{{ file_name ? file_name : 'Choose file...' }}</label>
              <div class="invalid-tooltip" v-if="fileError.required">Please include the Bank Identity File.</div>
              <div class="invalid-tooltip" v-if="fileError.fileSize">Bank Identity File may not be greater than 1 mb
              </div>
              <small>File needs to include BOTH sides of your identification in colour</small>
              <button
                v-if="file_name"
                type="button"
                class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
                @click="deleteAttachment"
              >
                <span class="sr-only">Delete File</span>
                <div class="btn-remove">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17">
                    <g fill="currentColor">
                      <path
                        d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/>
                      <path
                        d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/>
                      <path
                        d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/>
                    </g>
                  </svg>
                </div>
              </button>
            </div>

          </div>
        </div>

      </div>

      <div class="row">
        <div class="col-6 col-md-3 form-group save-details-button">
          <button class="btn btn-block mb-2 btn-primary" :disabled="submitStatus == 'PENDING'" type="submit">
            <div class="spinner-border" role="status" v-if="submitStatus == 'PENDING'">
              <span class="sr-only">Loading...</span>
            </div>
            <div v-if="submitStatus != 'PENDING' || !submitStatus">Save Details</div>
          </button>
        </div>
      </div>
    </div>
  </form>

</template>

<script>

import axios from "axios"
import { required, email, helpers } from 'vuelidate/lib/validators'
import Vue from 'vue'
import Vuelidate from 'vuelidate'

const bsbRegex = helpers.regex('bsbRegex', /^[[\d]{3}[-]{1}[\d]{3}$/)
const phoneRegex = helpers.regex('phoneRegex', /^([+][7][0-9]{10})|([+][6][1][0-9]{1,10})$/)
const abnRegex = helpers.regex('abnRegex', /^[\d]{11}$/)

Vue.use(Vuelidate)

const isValidFileSize = (value) => {
  if (!value) {
    return true
  }
  const file = value
  return (file.size < 1024 * 1024)
}

const isValidLogoSize = (value) => {
  if (!value) {
    return true
  }
  const file = value
  return (file.size < 512 * 512)
}

export default {
  name: 'CommitteeDetailsForm',
  props: ['committeeDetails', 'isLoading'],
  data() {
    return {
      errors: [],
      errorFields: {},
      errorStatus: null,
      zoneNotFound: false,
      zoneFound: false,
      zoneNotSpecified: false,
      zoneLocations: [],
      data: {
        post_code: '',
        zone_id: '',
        state: '',
        name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        abn: '',
        cheques_to: '',
        address: '',
        city: '',
        account_number: '',
        bsb: '',
        bank_abbreviation: '',
        gst_registered: '',
        bank_owner_first_name: '',
        bank_owner_last_name: '',
        bank_owner_dob: '',
        is_enabled_stripe: '',
        is_stripe_verified: '',
        logo_file_path: '',
        logo_file_name: ''
      },
      submitStatus: null,
      file_name: null,
      files: [],
      fileError: { fileSize: false, required: false },
      logoError: { logoSize: false, required: false },
      logo_file: [],
      logo_name: null
    }
  },
  validations() {
    const validationRules = {
      data: {
        state: {
          required
        },
        post_code: {
          required
        },
        zone_id: {
          required
        },
        name: {
          required
        },
        contact_email: {
          required, email
        },
        contact_phone: {
          required,
          phoneRegex
        },
        abn: {
          required,
          abnRegex
        },
        cheques_to: {
          required
        },
        pic_number: {},
        address: {
          required
        },
        city: {
          required
        },
        account_number: {
          required
        },
        bsb: {
          required,
          bsbRegex
        },
        contact_name: {
          required
        },
        bank_abbreviation: {},
        gst_registered: {}
      }
    }
    if (Number(this.data.is_enabled_stripe)) {
      validationRules.data.bank_owner_first_name = { required }
      validationRules.data.bank_owner_last_name = { required }
      validationRules.data.bank_owner_dob = { required }
    }
    return validationRules
  },
  created() {
    this.getZoneLocations()
  },
  watch: {
    committeeDetails() {
      this.data = { ...this.committeeDetails }
    }
  },
  methods: {
    async submit() {
      this.errorFields = {}
      this.fileError = { fileSize: false, required: false }
      this.errorStatus = null
      // eslint-disable-next-line eqeqeq
      if (this.data.is_enabled_stripe === '1' && !this.data.is_stripe_verified && !this.file) {
        this.submitStatus = 'ERROR'
        this.fileError.required = true
      } else if (!isValidFileSize(this.file)) {
        this.submitStatus = 'ERROR'
        this.fileError.fileSize = true
      }
      this.$v.$touch()
      if (this.$v.$invalid || this.fileError.required === true || this.fileError.fileSize) {
        this.submitStatus = 'ERROR'
      } else {
        // do your submit logic here
        this.submitStatus = 'PENDING'
        const formData = new FormData()
        formData.append('identity_file', this.file)
        formData.append('logo_file', this.logo_file)
        formData.append('fields', JSON.stringify(this.data))
        const result = await axios.post(`${this.$api}/committees/edit`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        if (result.data.status === 'success') {
          this.submitStatus = 'SUCCESS'
          this.data.is_stripe_verified = result.data.data.is_stripe_verified
          this.data.logo_file_name = result.data.data.logo_file_name
          this.data.logo_file_path = result.data.data.logo_file_path
          this.deleteLogoAttachment()
        } else {
          this.submitStatus = 'OK'
          this.errorStatus = 'Something went wrong. Please try again.'
        }
        if (result.data.status === 'validation_error') {
          this.submitStatus = 'ERROR'

          if (result.data.errors.contact_email !== undefined) {
            this.errorStatus = result?.data?.errors?.contact_email[0]
              ? result.data.errors.contact_email[0]
              : 'Committee email must be unique among committee and user emails'
            this.errorFields.contact_email = true
          } else if (result.data.errors.account_number !== undefined) {
            this.errorStatus = 'Committee account number is not valid'
            this.errorFields.account_number = true
          } else if (result.data.errors.routing_number !== undefined) {
            this.errorStatus = 'Committee BSB is not valid'
            this.errorFields.routing_number = true
          } else if (result.data.errors.postal_code !== undefined) {
            this.errorStatus = 'Committee postcode is not valid'
            this.errorFields.postal_code = true
          } else if (result.data.errors.city !== undefined) {
            this.errorStatus = 'Committee city is not valid'
            this.errorFields.city = true
          } else if (result.data.errors.line1 !== undefined) {
            this.errorStatus = 'Committee address file is not valid'
            this.errorFields.line1 = true
          } else if (result.data.errors.abn !== undefined) {
            this.errorStatus = 'Committee ABN is not valid'
            this.errorFields.abn = true
          } else {
            this.errorStatus = 'server validation error'
          }
        }
      }
    },
    findZone() {
      let coincidenceZones = []
      if (this.data?.city) {
        coincidenceZones = this.zoneLocations.filter(location => this.data?.city.toLowerCase() === location.suburb.toLowerCase())
      }
      if (this.data?.post_code) {
        // eslint-disable-next-line
        coincidenceZones = this.zoneLocations.filter((location) => {
          return this.data?.post_code === location.postcode && this.data?.city.toLowerCase() === location.suburb.toLowerCase()
        })
      }

      if (coincidenceZones.length === 1) {
        this.zoneNotFound = false
        this.zoneFound = true
        this.data.zone_id = coincidenceZones[0].zone_id
      } else if (coincidenceZones.length > 1) {
        this.zoneNotSpecified = true
        this.zoneFound = true
        this.zoneNotFound = false
      } else {
        this.zoneNotFound = true
        this.zoneFound = false
        this.zoneNotSpecified = false
      }
    },
    async getZoneLocations() {
      try {
        const { data: { data } } = await axios.get(`${this.$api}/zone-location/list`)
        this.zoneLocations = data || []
      } catch (error) {
        this.errorStatus = 'Can\'t fetch zone locations error'
      }
    },
    fileUpload(event) {
      this.fileError = { fileSize: false, required: false }
      this.file_name = ''
      const uploadedFile = event.target.files[0]
      this.file = uploadedFile
      if (uploadedFile) {
        this.file_name = uploadedFile.name
      }
    },
    logoUpload(event) {
      this.logoError = { logoSize: false, required: false }
      this.logo_name = ''
      console.log(event.target.files)
      const uploadedFile = event.target.files[0]
      this.logo_file = uploadedFile
      if (uploadedFile) {
        this.logo_name = uploadedFile.name
      }
      if (!isValidLogoSize(uploadedFile)) {
        this.submitStatus = 'ERROR'
        this.logoError.fileSize = true
        delete this.logo_file
        this.logo_file = undefined
        this.logo_name = ''
      }
    },
    deleteLogoAttachment() {
      delete this.logo_file
      this.logo_file = undefined
      this.logo_name = ''
    },
    deleteAttachment() {
      delete this.file
      this.file = undefined
      this.file_name = ''
    },
    deleteExistsAttachment() {
      this.data.logo_file_path = ''
      this.data.logo_file_name = ''
    },
    downloadLogoFile(id) {
      if (id) {
        axios.get(`${this.$api}/admin/committees/download/${id}`).then((response) => {
          window.open(response.data.path, '_blank')
        })
      }
    },
    validateBankBsb(event) {
      const value = event.target.value.replaceAll(/[^0-9]/g, '')
      const valueLength = value.length
      let newValue = value
      if (
        ((event.data === '-' && valueLength < 3) || event.inputType === 'insertFromPaste') ||
        ((event.data?.match(/[0-9]/) && valueLength === 3) || valueLength > 2)
      ) {
        newValue = `${value.replaceAll(/[^0-9]/g, '').slice(0, 3)}-${value.replaceAll(/[^0-9]/g, '').slice(3)}`
      }
      if (
        ((event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward') && valueLength === 3) ||
        valueLength < 3
      ) {
        newValue = value.replaceAll(/[^0-9]/g, '')
      }

      this.data.bsb = newValue
    }
  },
  computed: {
    zoneErrorMessage() {
      return this.warningMessage ? this.warningMessage : null
    }
  }
}
</script>

<style scoped>
.zone-alert {
  position: relative;
}

.gst_registered {
  display: flex;
  align-items: center;
}

.save-details-button {
  max-width: 180px;
  padding-top: 25px;
}
</style>



// WEBPACK FOOTER //
// src/views/Committees/Edit/components/CommitteeDetailsForm.vue